import React, { useState, useRef, useMemo } from 'react';
import { Dialog, Box, Button, IconButton, Typography, CircularProgress } from '@mui/material';
import styled, { keyframes, css } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QuizIcon from '@mui/icons-material/Quiz';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CodeIcon from '@mui/icons-material/Code';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
  parseWorkbook,
  validateRows,
  downloadTemplate,
  TYPES,
  TYPE_LABEL,
  COLUMNS,
  DIFFICULTIES,
  CODING_CATEGORIES,
  MAX_ROWS,
  MAX_FILE_MB,
  MIN_OPTIONS,
} from './questionImportSpec';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Shell ────────────────────────────────────────────────────────────────────
const Card = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 26px 28px 22px;
  max-height: 88vh;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #F3EEF9; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #D0BCFF; border-radius: 4px; }
`;
const Title = styled.div`
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 19px;
  color: #2D1B5E;
`;
const Subtitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  color: #8B7DB5;
  line-height: 1.55;
  margin-top: 2px;
`;
const CloseBtn = styled(IconButton)`
  && {
    color: #8B7DB5 !important;
    background: #F3EEF9 !important;
    border-radius: 10px !important;
    width: 34px !important;
    height: 34px !important;
    &:hover { background: #E8DEF8 !important; color: #4F378B !important; }
    svg { font-size: 17px; }
  }
`;

// ─── Instructions ─────────────────────────────────────────────────────────────
const SheetBlock = styled.div`
  border: 1px solid #EDE7F6;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 10px;
`;
const SheetHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  background: #F7F3FD;
  border-bottom: 1px solid #EDE7F6;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #4F378B;
  svg { font-size: 16px; }
`;
const SheetBody = styled.div`
  padding: 10px 14px 12px;
`;
const ColRow = styled.div`
  display: grid;
  grid-template-columns: 150px 66px 1fr;
  gap: 10px;
  align-items: baseline;
  padding: 4px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  color: #5C4A8A;
  line-height: 1.5;
`;
const ColName = styled.code`
  font-family: 'JetBrains Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  color: #2D1B5E;
  background: #F3EEF9;
  border-radius: 5px;
  padding: 2px 6px;
  justify-self: start;
`;
const ReqTag = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 10.5px;
  font-weight: 700;
  color: ${(p) => (p.$required ? '#B71C1C' : '#9C8FBE')};
`;
const Rule = styled.ul`
  margin: 0 0 14px;
  padding-left: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  color: #5C4A8A;
  line-height: 1.75;
  li::marker { color: #B0A0D0; }
`;

// ─── Drop zone ────────────────────────────────────────────────────────────────
const Drop = styled.div`
  border: 2px dashed ${(p) => (p.$over ? '#6750A4' : '#D0BCFF')};
  background: ${(p) => (p.$over ? '#F3EEF9' : '#FDFBFF')};
  border-radius: 14px;
  padding: 26px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: #6750A4; background: #F7F3FD; }
  &:focus-visible { outline: 2px solid #6750A4; outline-offset: 2px; }
`;
const DropTitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 13.5px;
  color: #4F378B;
  margin-top: 8px;
`;
const DropHint = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11.5px;
  color: #9C8FBE;
  margin-top: 3px;
`;

// ─── Report ───────────────────────────────────────────────────────────────────
const Tally = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;
const TallyChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1.5px solid ${(p) => p.$border};
  svg { font-size: 15px; }
`;
const Panel = styled.div`
  border: 1px solid ${(p) => p.$border || '#EDE7F6'};
  background: ${(p) => p.$bg || '#FDFBFF'};
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  ${css`animation: ${fadeUp} 0.2s ease both;`}
`;
const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$color || '#4F378B'};
  margin-bottom: 8px;
  svg { font-size: 15px; }
`;
const IssueRow = styled.div`
  display: grid;
  grid-template-columns: 74px 128px 1fr;
  gap: 10px;
  padding: 5px 0;
  border-top: 1px solid rgba(0,0,0,0.04);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  color: #4A3A6B;
  line-height: 1.5;
  &:first-of-type { border-top: none; }
`;
const RowRef = styled.span`
  font-family: 'JetBrains Mono', 'Fira Mono', monospace;
  font-size: 11px;
  color: #6750A4;
  font-weight: 600;
`;
const FieldRef = styled.span`
  font-weight: 700;
  color: #2D1B5E;
`;
const ScrollBox = styled.div`
  max-height: 190px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #D0BCFF; border-radius: 4px; }
`;
const PreviewItem = styled.div`
  padding: 7px 0;
  border-top: 1px solid #F0EAF9;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  color: #2D1B5E;
  line-height: 1.5;
  display: flex;
  gap: 9px;
  &:first-of-type { border-top: none; }
`;
const PreviewNum = styled.span`
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 11px;
  color: #6750A4;
  min-width: 20px;
`;
const PreviewMeta = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  color: #8B7DB5;
  margin-top: 2px;
`;

const GhostBtn = styled(Button)`
  && {
    background: transparent !important;
    color: #6750A4 !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 700 !important;
    font-size: 12.5px !important;
    border-radius: 10px !important;
    padding: 8px 16px !important;
    text-transform: none !important;
    border: 1.5px solid rgba(103,80,164,0.35) !important;
    &:hover { background: rgba(103,80,164,0.06) !important; border-color: #6750A4 !important; }
    svg { font-size: 16px; }
  }
`;
const SolidBtn = styled(Button)`
  && {
    background: ${(p) => (p.disabled ? '#EEE' : 'linear-gradient(135deg, #4F378B, #6750A4)')} !important;
    color: ${(p) => (p.disabled ? '#aaa' : '#fff')} !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 700 !important;
    font-size: 12.5px !important;
    border-radius: 10px !important;
    padding: 8px 20px !important;
    text-transform: none !important;
    box-shadow: ${(p) => (p.disabled ? 'none' : '0 4px 14px rgba(79,55,139,0.3)')} !important;
    svg { font-size: 16px; }
  }
`;

const TYPE_ICON = {
  mcq: <QuizIcon />,
  subjective: <MenuBookIcon />,
  coding: <CodeIcon />,
};

// Short human explanation per column, keyed by the template header.
const COLUMN_NOTES = {
  'Question': 'The question text.',
  'Option A': `At least ${MIN_OPTIONS} options must be filled. E and F are optional extras.`,
  'Correct Answer': 'A letter (B), several letters (A, C), or the full option text.',
  'Difficulty': DIFFICULTIES.join(', ') + ' — anything else is rejected.',
  'Reference Answer': 'The model answer graders compare against.',
  'Key Points': 'At least one. One per line (Alt+Enter) or separated by |',
  'Skill': 'e.g. RTOS Concepts, React, SQL.',
  'Sub Topic': 'e.g. pointer arithmetic and type casting.',
  'Question Angle': 'e.g. explain how/why, compare A vs B.',
  'Category': CODING_CATEGORIES.join(', ') + '. Blank means Coding.',
  'Language': 'e.g. C, Python, Java.',
  'Problem Statement': 'Category Coding only.',
  'Constraints': 'Category Coding only, e.g. 1 <= n <= 10^5',
  'Input Format': 'Category Coding only.',
  'Output Format': 'Category Coding only.',
  'Example 1 Input': 'Category Coding only. Input and Output both needed.',
  'Code Scenario': 'Category Debugging only.',
  'Scenario': 'Category Tooling only.',
  'Task': 'Category Tooling only.',
};

// Columns shown in the in-app summary. The template carries the full set.
const SUMMARY_COLUMNS = {
  mcq:        ['Question', 'Option A', 'Correct Answer', 'Difficulty'],
  subjective: ['Question', 'Skill', 'Difficulty', 'Reference Answer', 'Key Points', 'Sub Topic', 'Question Angle'],
  coding:     ['Difficulty', 'Skill', 'Language', 'Category', 'Problem Statement', 'Constraints',
               'Input Format', 'Output Format', 'Example 1 Input', 'Code Scenario', 'Scenario', 'Task'],
};

// Required for every row of that type.
const REQUIRED_HEADERS = {
  mcq:        new Set(['Question', 'Option A', 'Correct Answer', 'Difficulty']),
  subjective: new Set(['Question', 'Skill', 'Difficulty', 'Reference Answer', 'Key Points']),
  coding:     new Set(['Difficulty', 'Skill', 'Language']),
};

// Required only for certain coding categories — shown as a conditional tag.
const CONDITIONAL_HEADERS = {
  'Problem Statement': 'Coding',
  'Constraints':       'Coding',
  'Input Format':      'Coding',
  'Output Format':     'Coding',
  'Example 1 Input':   'Coding',
  'Code Scenario':     'Debugging',
  'Scenario':          'Tooling',
  'Task':              'Tooling',
};

const previewLabel = (type, q) => {
  if (type === 'mcq') return q.question;
  if (type === 'subjective') return q.question;
  return q.problem_statement || q.question || q.scenario || q.task || q.code_scenario;
};

const previewMeta = (type, q) => {
  const bits = [];
  if (type === 'coding' && q.category) bits.push(q.category);
  if (q.difficulty) bits.push(q.difficulty);
  if (q.skill) bits.push(q.skill);
  if (type === 'mcq') bits.push(`${q.options.length} options`, `${q.correct_answer.length} correct`);
  if (type === 'subjective' && q.key_points?.length) bits.push(`${q.key_points.length} key points`);
  if (type === 'coding' && q.examples?.length) bits.push(`${q.examples.length} examples`);
  return bits.join(' · ');
};

/**
 * Bulk import of questions from a spreadsheet.
 *
 * @param {string}   jdId                JD identifier (jdID string, same one the other endpoints use)
 * @param {Function} onClose             close handler
 * @param {Function} onImported          called after a successful import so the parent can refetch
 * @param {Function} logActivity         optional activity logger from the parent
 * @param {Object}   existing            { mcq: [], subjective: [], coding: [] } current questions, for duplicate detection
 */
const ImportQuestionsModal = ({ jdId, onClose, onImported, logActivity, existing = {} }) => {
  const [stage, setStage]       = useState('instructions'); // instructions | report | done
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fatal, setFatal]       = useState('');
  const [notices, setNotices]   = useState([]);
  const [result, setResult]     = useState(null);   // per-type validation output
  const [outcome, setOutcome]   = useState(null);   // server response after import
  const inputRef = useRef(null);

  const IMPORT_BASE = process.env.REACT_APP_REMOVAL_LOG_URL;

  const totals = useMemo(() => {
    if (!result) return { valid: 0, errors: 0, warnings: 0, duplicates: 0 };
    return TYPES.reduce(
      (acc, t) => ({
        valid:      acc.valid      + result[t].valid.length,
        errors:     acc.errors     + result[t].errors.length,
        warnings:   acc.warnings   + result[t].warnings.length,
        duplicates: acc.duplicates + result[t].duplicates.length,
      }),
      { valid: 0, errors: 0, warnings: 0, duplicates: 0 }
    );
  }, [result]);

  const reset = () => {
    setStage('instructions');
    setFileName('');
    setFatal('');
    setNotices([]);
    setResult(null);
    setOutcome(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = async (file) => {
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setFatal(`"${file.name}" is a .${ext} file. Choose an .xlsx, .xls or .csv file.`);
      setStage('report');
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setFatal(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${MAX_FILE_MB} MB.`);
      setStage('report');
      return;
    }

    setParsing(true);
    setFileName(file.name);
    setFatal('');
    setNotices([]);

    try {
      const buffer = await file.arrayBuffer();
      const { rows, notices: parseNotices, fatal: parseFatal } = parseWorkbook(buffer);

      if (parseFatal) {
        setFatal(parseFatal);
        setStage('report');
        return;
      }

      const totalRows = TYPES.reduce((n, t) => n + rows[t].length, 0);
      if (totalRows === 0) {
        setFatal('The sheets were found but every row is empty. Add your questions under the header row.');
        setStage('report');
        return;
      }
      if (totalRows > MAX_ROWS) {
        setFatal(`This file holds ${totalRows} rows. The limit is ${MAX_ROWS} per import — split it into smaller files.`);
        setStage('report');
        return;
      }

      const validated = {};
      for (const type of TYPES) {
        validated[type] = validateRows(type, rows[type], existing[type] || []);
      }

      setNotices(parseNotices);
      setResult(validated);
      setStage('report');
    } catch (err) {
      console.error('Import parse failed:', err);
      setFatal(`The file could not be read (${err.message}). Try re-saving it as .xlsx.`);
      setStage('report');
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!result || totals.valid === 0) return;
    setImporting(true);
    try {
      const payload = {
        mcq:        result.mcq.valid,
        subjective: result.subjective.valid,
        coding:     result.coding.valid,
      };

      const res = await fetch(`${IMPORT_BASE}/${jdId}/bulk-add-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || `Import failed (${res.status})`);

      setOutcome(data);
      setStage('done');

      if (typeof logActivity === 'function') {
        logActivity('IMPORT_QUESTIONS', {
          targetName: `Imported ${data.inserted?.total ?? totals.valid} question(s) from ${fileName}`,
          metadata: { fileName, inserted: data.inserted, skipped: data.skipped?.length || 0 },
        });
      }
      if (typeof onImported === 'function') onImported();
    } catch (err) {
      console.error('Bulk import failed:', err);
      setFatal(err.message);
      setStage('report');
    } finally {
      setImporting(false);
    }
  };

  // ── Instructions stage ─────────────────────────────────────────────────────
  const renderInstructions = () => (
    <>
      <Rule>
        <li>One question per row, in the sheet matching its type. Leave a sheet empty if you have none of that type.</li>
        <li>Keep the header row exactly as it is. Extra columns are ignored, so you can keep your own notes alongside.</li>
        <li>Fill every column marked required below. A row missing one is listed with the reason and not imported.</li>
        <li>Every row is checked before anything is saved — you will see what is ready and what needs fixing.</li>
        <li>Up to {MAX_ROWS} questions per file, {MAX_FILE_MB} MB maximum.</li>
      </Rule>

      {TYPES.map((type) => (
        <SheetBlock key={type}>
          <SheetHead>
            {TYPE_ICON[type]} {TYPE_LABEL[type]} sheet
          </SheetHead>
          <SheetBody>
            {SUMMARY_COLUMNS[type].map((header) => {
              const col = COLUMNS[type].find((c) => c.header === header);
              if (!col) return null;
              const required = REQUIRED_HEADERS[type].has(header);
              const conditional = CONDITIONAL_HEADERS[header];
              const tag = required ? 'required' : conditional ? `if ${conditional}` : 'optional';
              return (
                <ColRow key={header}>
                  <ColName>{header}</ColName>
                  <ReqTag $required={required || !!conditional}>{tag}</ReqTag>
                  <span>{COLUMN_NOTES[header] || ''}</span>
                </ColRow>
              );
            })}
            {type === 'mcq' && (
              <ColRow>
                <ColName>Option B – D</ColName>
                <ReqTag $required>required</ReqTag>
                <span>{MIN_OPTIONS} options minimum. Option E and F are optional.</span>
              </ColRow>
            )}
            {type === 'coding' && (
              <ColRow>
                <ColName>+ 13 more</ColName>
                <ReqTag>optional</ReqTag>
                <span>Problem Theme, Examples 2–3, Observed Behavior, Expected Approach, Expected Discussion Points and others — all listed in the template.</span>
              </ColRow>
            )}
          </SheetBody>
        </SheetBlock>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1.5 }}>
        <GhostBtn startIcon={<DownloadIcon />} onClick={() => downloadTemplate()}>
          Download template
        </GhostBtn>
        <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#8B7DB5' }}>
          Comes with the headers, a filled example of each type, and the allowed values.
        </Typography>
      </Box>

      <Drop
        role="button"
        tabIndex={0}
        $over={dragOver}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
      >
        {parsing ? (
          <>
            <CircularProgress size={26} sx={{ color: '#6750A4' }} />
            <DropTitle>Checking {fileName}</DropTitle>
          </>
        ) : (
          <>
            <UploadFileIcon sx={{ fontSize: 32, color: '#B0A0D0' }} />
            <DropTitle>Choose a file or drop it here</DropTitle>
            <DropHint>.xlsx, .xls or .csv</DropHint>
          </>
        )}
      </Drop>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </>
  );

  // ── Report stage ───────────────────────────────────────────────────────────
  const renderReport = () => {
    if (fatal) {
      return (
        <Panel $bg="#FFF5F5" $border="#FFCDD2">
          <PanelHead $color="#B71C1C"><ErrorOutlineIcon /> This file can't be imported</PanelHead>
          <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 13, color: '#7f0000', lineHeight: 1.6 }}>
            {fatal}
          </Typography>
        </Panel>
      );
    }

    return (
      <>
        <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12.5, color: '#8B7DB5', mb: 1.5 }}>
          {fileName}
        </Typography>

        <Tally>
          <TallyChip $bg="#E8F5E9" $fg="#1B5E20" $border="#A5D6A7">
            <CheckCircleIcon /> {totals.valid} ready
          </TallyChip>
          {totals.errors > 0 && (
            <TallyChip $bg="#FFEBEE" $fg="#B71C1C" $border="#FFCDD2">
              <ErrorOutlineIcon /> {totals.errors} need fixing
            </TallyChip>
          )}
          {totals.duplicates > 0 && (
            <TallyChip $bg="#EDE7F6" $fg="#4F378B" $border="#D0BCFF">
              <ContentCopyIcon /> {totals.duplicates} duplicate
            </TallyChip>
          )}
          {totals.warnings > 0 && (
            <TallyChip $bg="#FFF8E1" $fg="#7B5E00" $border="#FFD54F">
              <WarningAmberIcon /> {totals.warnings} to check
            </TallyChip>
          )}
        </Tally>

        {notices.map((n, i) => (
          <Panel key={i} $bg="#F7F3FD" $border="#E8DEF8">
            <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#5C4A8A' }}>{n}</Typography>
          </Panel>
        ))}

        {TYPES.map((type) => {
          const r = result[type];
          if (!r.errors.length) return null;
          return (
            <Panel key={`e-${type}`} $bg="#FFF5F5" $border="#FFCDD2">
              <PanelHead $color="#B71C1C">
                <ErrorOutlineIcon /> {TYPE_LABEL[type]} sheet — {r.errors.length} row{r.errors.length > 1 ? 's' : ''} not imported
              </PanelHead>
              <ScrollBox>
                {r.errors.map((e, i) => (
                  <IssueRow key={i}>
                    <RowRef>Row {e.row}</RowRef>
                    <FieldRef>{e.field}</FieldRef>
                    <span>{e.message}</span>
                  </IssueRow>
                ))}
              </ScrollBox>
            </Panel>
          );
        })}

        {TYPES.map((type) => {
          const r = result[type];
          if (!r.duplicates.length) return null;
          return (
            <Panel key={`d-${type}`} $bg="#FAF7FF" $border="#E8DEF8">
              <PanelHead $color="#4F378B">
                <ContentCopyIcon /> {TYPE_LABEL[type]} sheet — {r.duplicates.length} duplicate{r.duplicates.length > 1 ? 's' : ''} skipped
              </PanelHead>
              <ScrollBox>
                {r.duplicates.map((d, i) => (
                  <IssueRow key={i}>
                    <RowRef>Row {d.row}</RowRef>
                    <FieldRef>{d.reason}</FieldRef>
                    <span>{d.preview}…</span>
                  </IssueRow>
                ))}
              </ScrollBox>
            </Panel>
          );
        })}

        {TYPES.map((type) => {
          const r = result[type];
          if (!r.warnings.length) return null;
          return (
            <Panel key={`w-${type}`} $bg="#FFFCF0" $border="#FFE082">
              <PanelHead $color="#7B5E00">
                <WarningAmberIcon /> {TYPE_LABEL[type]} sheet — worth a look, will still import
              </PanelHead>
              <ScrollBox>
                {r.warnings.map((w, i) => (
                  <IssueRow key={i}>
                    <RowRef>Row {w.row}</RowRef>
                    <FieldRef>{w.field}</FieldRef>
                    <span>{w.message}</span>
                  </IssueRow>
                ))}
              </ScrollBox>
            </Panel>
          );
        })}

        {TYPES.map((type) => {
          const r = result[type];
          if (!r.valid.length) return null;
          return (
            <Panel key={`v-${type}`} $bg="#FBFFFC" $border="#C8E6C9">
              <PanelHead $color="#1B5E20">
                {TYPE_ICON[type]} {r.valid.length} {TYPE_LABEL[type]} question{r.valid.length > 1 ? 's' : ''} ready
              </PanelHead>
              <ScrollBox>
                {r.valid.slice(0, 40).map((q, i) => (
                  <PreviewItem key={i}>
                    <PreviewNum>{i + 1}</PreviewNum>
                    <div>
                      {String(previewLabel(type, q)).slice(0, 150)}
                      {String(previewLabel(type, q)).length > 150 ? '…' : ''}
                      <PreviewMeta>{previewMeta(type, q)}</PreviewMeta>
                    </div>
                  </PreviewItem>
                ))}
                {r.valid.length > 40 && (
                  <PreviewMeta style={{ paddingTop: 8 }}>+ {r.valid.length - 40} more</PreviewMeta>
                )}
              </ScrollBox>
            </Panel>
          );
        })}

        {totals.valid === 0 && (
          <Panel $bg="#FFF5F5" $border="#FFCDD2">
            <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 13, color: '#7f0000', lineHeight: 1.6 }}>
              No rows are ready to import. Fix the rows listed above and choose the file again.
            </Typography>
          </Panel>
        )}
      </>
    );
  };

  // ── Done stage ─────────────────────────────────────────────────────────────
  const renderDone = () => (
    <Panel $bg="#F1FAF3" $border="#C8E6C9">
      <PanelHead $color="#1B5E20"><CheckCircleIcon /> Import finished</PanelHead>
      <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 13, color: '#1a3d23', lineHeight: 1.7 }}>
        Added {outcome?.inserted?.mcq ?? 0} MCQ, {outcome?.inserted?.subjective ?? 0} Theory and{' '}
        {outcome?.inserted?.coding ?? 0} Coding question{(outcome?.inserted?.total ?? 0) === 1 ? '' : 's'} to this JD.
      </Typography>
      {outcome?.skipped?.length > 0 && (
        <>
          <PanelHead $color="#7B5E00" style={{ marginTop: 12 }}>
            <WarningAmberIcon /> {outcome.skipped.length} rejected on the server
          </PanelHead>
          <ScrollBox>
            {outcome.skipped.map((s, i) => (
              <IssueRow key={i}>
                <RowRef>{TYPE_LABEL[s.type] || s.type}</RowRef>
                <FieldRef>#{s.index + 1}</FieldRef>
                <span>{s.reason}</span>
              </IssueRow>
            ))}
          </ScrollBox>
        </>
      )}
    </Panel>
  );

  return (
    <Dialog
      open
      onClose={importing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '18px', boxShadow: '0 24px 60px rgba(79,55,139,0.25)', overflow: 'visible' } }}
    >
      <Card>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Title>Import questions from a spreadsheet</Title>
            <Subtitle>
              {stage === 'instructions' && 'Fill the template, choose the file, and review what will be added before it is saved.'}
              {stage === 'report'       && 'Review the check below, then add the ready rows to this JD.'}
              {stage === 'done'         && 'The question bank has been updated.'}
            </Subtitle>
          </Box>
          <CloseBtn onClick={onClose} size="small" disabled={importing}><CloseIcon /></CloseBtn>
        </Box>

        {stage === 'instructions' && renderInstructions()}
        {stage === 'report'       && renderReport()}
        {stage === 'done'         && renderDone()}

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          {stage === 'report' && (
            <GhostBtn startIcon={<ArrowBackIcon />} onClick={reset} disabled={importing}>
              Choose another file
            </GhostBtn>
          )}
          {stage !== 'done' && (
            <GhostBtn onClick={onClose} disabled={importing}>Cancel</GhostBtn>
          )}
          {stage === 'report' && !fatal && (
            <SolidBtn disabled={totals.valid === 0 || importing} onClick={handleImport}>
              {importing
                ? 'Adding…'
                : <><CheckCircleIcon sx={{ mr: 0.6 }} /> Add {totals.valid} question{totals.valid === 1 ? '' : 's'}</>}
            </SolidBtn>
          )}
          {stage === 'done' && (
            <SolidBtn onClick={onClose}>Done</SolidBtn>
          )}
        </Box>
      </Card>
    </Dialog>
  );
};

export default ImportQuestionsModal;